import React, { useState, useEffect } from "react";
import moment from "moment";
import { motion, AnimatePresence } from "framer-motion";
import TipTapEditor from "../../../../../../components/TipTapEditor";
import api from "../../../../../../services/api";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { RiSendPlaneFill, RiEmotionHappyLine } from "react-icons/ri";
import { FaTrash, FaRegClock, FaBold, FaItalic, FaLink } from "react-icons/fa";

export default ({ value, setValue, iaResponse, setIaResponse }) => {
  const [comment, setComment] = useState("");
  const [comments, setComments] = useState(value.comments || []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useSelector((state) => state.Auth.user);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setIsSubmitting(true);
    try {
      await onAdd(comment);
      setComment("");
    } catch (error) {
      toast.error("Failed to send comment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const onAdd = async (e) => {
    try {
      const newComment = {
        text: e,
        user_email: user.email,
        user_name: user.name,
        user_id: user._id,
        user_avatar: user.avatar,
        created_at: new Date().toISOString(),
      };

      const newComments = [...comments, newComment];

      const { ok } = await api.post(`/roadlog_item/${value._id}/send-email`, { comment: newComment });
      if (!ok) return toast.error("Failed to send email");

      const { data } = await api.put(`/roadlog_item/${value._id}`, { comments: newComments });
      setValue(data);
      if (user._id === value.user_id) return;
      await api.post(`/roadlog_item/${value._id}/send-comment`, { comment: newComment });
    } catch (error) {
      console.error("Error sending email:", error);
      throw error;
    }
  };

  const onDelete = async (id) => {
    try {
      const newComments = comments.filter((c) => c._id !== id);
      const { data } = await api.put(`/roadlog_item/${value._id}`, { comments: newComments });
      setValue(data);
      toast.success("Comment deleted!");
    } catch (error) {
      toast.error("Failed to delete comment");
    }
  };

  useEffect(() => {
    setComments(value.comments || []);
  }, [value.comments]);

  useEffect(() => {
    if (iaResponse) {
      setComment(iaResponse);
      setIaResponse(""); // Reset after setting
    }
  }, [iaResponse, setIaResponse]);

  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* Comment Input */}
      <div className="bg-transparent">
        <form onSubmit={handleSubmit}>
          <div className="rounded-xl overflow-hidden border border-gray-200">
            <div className="bg-white border-b border-gray-100">
              <TipTapEditor
                buttonList={["bold", "italic", "link", "image", "bulletList", "orderedList", "blockquote", "codeBlock"]}
                values={comment}
                onChange={(e) => setComment(e)}
                placeholder="Share your thoughts..."
              />
            </div>

            <div className="px-3 py-2 bg-gradient-to-r from-gray-50 to-white flex justify-end items-center">
              <button
                type="submit"
                disabled={!comment.trim() || isSubmitting}
                className={`px-4 py-1.5 rounded-full flex items-center gap-2 text-sm font-medium transition-all
                           ${
                             isSubmitting
                               ? "bg-gray-100 text-gray-400"
                               : "bg-gradient-to-r from-indigo-600 to-blue-500 text-white hover:from-indigo-700 hover:to-blue-600 shadow-sm"
                           }`}>
                <span>{isSubmitting ? "Sending..." : "Comment"}</span>
                <RiSendPlaneFill className={`h-4 w-4 ${isSubmitting ? "animate-pulse" : ""}`} />
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Comments Thread */}
      <div className="flex-1 overflow-y-auto mt-4 space-y-5">
        <div className="flex items-center justify-between mb-1 px-1">
          <h3 className="text-sm font-medium text-gray-500">Discussion ({comments.length})</h3>
          <div className="h-px bg-gray-200 flex-grow mx-4"></div>
        </div>

        <AnimatePresence initial={false}>
          {(comments || [])
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            .map((comment, index) => (
              <motion.div
                key={comment._id || index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0, marginBottom: 0 }}
                transition={{ duration: 0.2 }}
                className="group relative bg-white rounded-xl shadow-sm border border-gray-100 transition-all overflow-hidden">
                <div className="flex items-start p-4">
                  {/* Avatar column */}
                  <div className="mr-3 flex-shrink-0">
                    <div className="relative">
                      <img src={comment.user_avatar} className="w-10 h-10 rounded-full border-2 border-white shadow-sm object-cover" alt={comment.user_name} />
                      <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white"></div>
                    </div>
                  </div>

                  {/* Content column */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col items-start justify-between pb-2">
                      <div className="font-medium text-gray-900 mb-0">{comment.user_name}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 mb-1" title={new Date(comment.created_at).toLocaleString()}>
                        <FaRegClock className="h-3 w-3" />
                        <span>{moment(new Date(comment.created_at)).fromNow()}</span>
                      </div>
                      <div className="prose prose-sm max-w-none comment-content text-gray-700" dangerouslySetInnerHTML={{ __html: comment.text }} />
                    </div>
                  </div>
                </div>
                {/* Delete button (visible on hover) */}
                {(!comment.user_id || comment.user_id === user._id) && (
                  <button
                    className="absolute top-3 right-3 p-1.5 rounded-full bg-white/90 backdrop-blur-sm text-gray-400 
                              hover:text-red-500 hover:bg-red-50 transition-all"
                    onClick={() => {
                      if (window.confirm("Delete this comment?")) {
                        onDelete(comment._id);
                      }
                    }}>
                    <FaTrash className="w-3 h-3" />
                  </button>
                )}
              </motion.div>
            ))}
        </AnimatePresence>

        {comments.length === 0 && (
          <div className="text-center py-10 px-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <RiSendPlaneFill className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-gray-500 font-medium">No comments yet</p>
            <p className="text-sm text-gray-400 mt-1">Be the first to share your thoughts!</p>
          </div>
        )}
      </div>
    </div>
  );
};
